import { useReactiveQuery } from '@pogokid/dblite/react'
import {
  DOMAIN_EVENT_REF_ALL_SYNC_STATES,
  DomainEventRef,
  DomainEventRefSyncState,
  ProjectionStateRecord
} from '@soniq/public-resource'
import { useAuthContext } from '@soniq/util-auth'
import { useDb } from '../db.ts'
import { Optional } from '@pogokid/util'

export function useDomainEventRefs() {
  const { db } = useDb()
  return useReactiveQuery<DomainEventRef>({
    query: db
      .selectFrom('domain_event_refs')
      .selectAll()
      .orderBy('last_pull', 'desc')
      .compile(),
    enabled: true,
    channel: 'domain_event_ref',
  })
}

export function useDomainEventRefForProfile(
  profileId: Optional<string>
) {
  const { db } = useDb()
  const res = useReactiveQuery<DomainEventRef>({
    query: db
      .selectFrom('domain_event_refs')
      .selectAll()
      .where('profile_id', '=', profileId ?? '')
      .compile(),
    enabled: !!profileId,
    channel: 'domain_event_ref',
  })
  return {
    ...res,
    value: res.value?.[0] ?? null,
  }
}

export function useDomainEventRefsLatestPulled() {
  const { value, ...rest } = useDomainEventRefs()
  const latest = value?.reduce((mem, ref) => {
    if (!mem) return ref
    const memLastPull = new Date(mem.last_pull ?? 0)
    const refLastPull = new Date(ref.last_pull ?? 0)
    if (refLastPull > memLastPull) {
      return ref
    }
    return mem
  }, undefined as DomainEventRef | undefined)

  return {
    ...rest,
    value: latest,
  }
}

export function useDomainEventRefsLatestSyncState() {
  const { accessTokenState } = useAuthContext()
  const { value: latest } = useDomainEventRefsLatestPulled()
  return latest || accessTokenState === 'active'
    ? latest?.sync_state ?? 'syncing'
    : 'idle'
}

export type ProjectionStateWithLatest = ProjectionStateRecord & {
  latest_device_sequence: number | null
}

/**
 * Every projection's progress on this device, alongside the latest
 * local event for the same aggregate type and profile, so you can
 * see how far behind each projection is.
 */
export function useProjectionStates() {
  const { db } = useDb()
  return useReactiveQuery<ProjectionStateWithLatest>({
    query: db
      .selectFrom('projection_state')
      .selectAll('projection_state')
      .select((eb) =>
        eb
          .selectFrom('domain_event')
          .select((sub) =>
            sub.fn
              .max('domain_event.device_sequence_number')
              .as('latest')
          )
          .whereRef(
            'domain_event.aggregate_type',
            '=',
            'projection_state.aggregate_type'
          )
          .whereRef(
            'domain_event.profile_id',
            '=',
            'projection_state.profile_id'
          )
          .as('latest_device_sequence')
      )
      .orderBy('projection_state.last_updated_at', 'desc')
      .compile(),
    enabled: true,
    channel: 'projection_state',
  })
}

export function useProjectionStateForProfile(
  profileId: Optional<string>
) {
  const { db } = useDb()
  const res = useReactiveQuery<
    ProjectionStateRecord & { sequence_number: number | null }
  >({
    query: db
      .selectFrom('projection_state')
      .innerJoin(
        'domain_event',
        'domain_event.device_sequence_number',
        'projection_state.last_processed_sequence'
      )
      .selectAll('projection_state')
      .select('domain_event.sequence_number')
      .where('projection_state.profile_id', '=', profileId ?? '')
      .compile(),
    enabled: !!profileId,
    channel: 'projection_state',
  })
  return {
    ...res,
    value: res.value?.[0] ?? null,
  }
}

export interface SyncHealth {
  code: string | null
  globalState: DomainEventRefSyncState
  syncState?: DomainEventRefSyncState
  projectionState?: DomainEventRefSyncState
  hasEventsNotPushed: boolean
  latestSyncedSequence: number | null
  latestProcessedSequence: number | null
}

export function useSyncHealthForProfile(
  profileId: Optional<string>
): SyncHealth {
  const { value: domainEventRef } =
    useDomainEventRefForProfile(profileId)
  const { value: projectionState } =
    useProjectionStateForProfile(profileId)

  const syncState = domainEventRef?.sync_state
  const latestSyncedSequence =
    domainEventRef?.last_seen_sequence ?? null
  const latestProcessedSequence =
    projectionState?.sequence_number ?? null
  const projState = projectionState?.projection_state
  const hasEventsNotPushed = latestProcessedSequence === null

  return {
    code: domainEventRef?.message ?? projectionState?.message ?? null,
    globalState: DOMAIN_EVENT_REF_ALL_SYNC_STATES.find((s) => [syncState, projState].includes(s)) ?? 'idle',
    syncState,
    projectionState: projState,
    hasEventsNotPushed,
    latestSyncedSequence,
    latestProcessedSequence,
  }
}
